import { Component, Input } from '@angular/core';

import {
    BalanceSnapshot,
    RedemptionLegRow,
    balanceAmount,
    isBalanceAvailable,
    UNAVAILABLE_DISPLAY,
} from 'src/app/model/cashback-allocation-balance';

/**
 * LP-5358 / LP-5360: the supplier legs of an RMS cashback REDEMPTION, shared by the admin and
 * Enterprise transaction detail views.
 *
 * <p>A separate component from {@code CashbackAllocationTableComponent} rather than a mode of it,
 * because the two describe OPPOSITE movements: an allocation credits the trader's per-supplier
 * cashback, a redemption draws it down. Merging them would put credits and debits in one column
 * with only {@code mutationType} to tell them apart. They share a stylesheet, not a template.
 *
 * <p>Extracted in LP-5360 so both views render one implementation. It was previously inline in the
 * admin template, which is exactly how two views of one ledger drift -- and it had already drifted
 * once: the inline version carried no {@code data-label} attributes, so its rows would have become
 * unlabelled cards on mobile.
 *
 * <p><b>Movement-neutral.</b> {@code mutationType} is rendered verbatim and never interpreted.
 * The backend selects which row to show and the portal never nets one against another.
 *
 * <p><b>And deliberately not presented as a lifecycle.</b> The backend's
 * {@code loadLatestReserveSnapshots} skips every snapshot whose type is not {@code RESERVE}, so
 * the Movement column can only ever read {@code RESERVE} or the unavailable marker --
 * {@code CAPTURE} has no snapshot by design, and a {@code RELEASE} or {@code REVERSE} never
 * reaches this DTO. The copy therefore states that a reservation is not completed spending and
 * that later entries are not shown. Describing what a RELEASE "would mean here" implied the table
 * would surface one, and heading it "per-supplier movement" implied a movement log; a recalculated
 * checkout writes RESERVE/RELEASE/RESERVE under one owner reference and only the surviving
 * reservation appears. Over-stating completeness misleads just as surely as asserting a direction.
 */
@Component({
    selector: 'app-cashback-redemption-table',
    templateUrl: './cashback-redemption-table.component.html',
    styleUrls: ['../styles/cashback-allocation-table.css'],
})
export class CashbackRedemptionTableComponent {
    /** The redemption legs to render, in the order the API sent them. */
    @Input() rows: RedemptionLegRow[] = [];

    unavailableDisplay = UNAVAILABLE_DISPLAY;

    hasBalance(snapshot: BalanceSnapshot): boolean {
        return isBalanceAvailable(snapshot);
    }

    balanceValue(snapshot: BalanceSnapshot): number | null {
        return balanceAmount(snapshot);
    }
}
